
import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { ArrowRight, Check, Share2, Users, Award, BarChart4 } from 'lucide-react';

const FeatureCard: React.FC<{ title: string; description: string; icon: React.ReactNode }> = ({ 
  title, 
  description, 
  icon 
}) => (
  <div className="bg-white p-6 rounded-lg shadow-md transition-all hover:shadow-lg flex flex-col items-center text-center">
    <div className="bg-blue-100 p-3 rounded-full mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-2 text-primary-blue">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

const HowItWorks: React.FC = () => {
  return (
    <MainLayout isLoggedIn={false}>
      <div className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-primary-blue mb-4">How Our Referral Platform Works</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our powerful referral platform makes it easy to create, manage, and track referral programs 
              that grow your business.
            </p>
          </div>
          
          {/* Step-by-Step Process */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-10 text-primary-blue">Simple 3-Step Process</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="bg-white p-8 rounded-lg shadow-md relative">
                <div className="bg-primary-blue text-white text-xl font-bold h-12 w-12 flex items-center justify-center rounded-full absolute -top-6 left-8">
                  1
                </div>
                <h3 className="text-xl font-semibold mb-4 mt-6 text-primary-blue">Set Up Your Program</h3>
                <p className="text-gray-600 mb-4">
                  Define your rewards, customize your messaging, and design your referral landing pages to match your brand.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Easy-to-use dashboard</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Customizable templates</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Flexible reward structures</span>
                  </li>
                </ul>
              </div>
              
              {/* Step 2 */}
              <div className="bg-white p-8 rounded-lg shadow-md relative">
                <div className="bg-primary-blue text-white text-xl font-bold h-12 w-12 flex items-center justify-center rounded-full absolute -top-6 left-8">
                  2
                </div>
                <h3 className="text-xl font-semibold mb-4 mt-6 text-primary-blue">Invite Referrers</h3>
                <p className="text-gray-600 mb-4">
                  Invite your customers, partners, or team members to join your referral program and start sharing.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Bulk invitations</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Automated onboarding</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">User-friendly portals</span>
                  </li>
                </ul>
              </div>
              
              {/* Step 3 */}
              <div className="bg-white p-8 rounded-lg shadow-md relative">
                <div className="bg-primary-blue text-white text-xl font-bold h-12 w-12 flex items-center justify-center rounded-full absolute -top-6 left-8">
                  3
                </div>
                <h3 className="text-xl font-semibold mb-4 mt-6 text-primary-blue">Track & Reward</h3>
                <p className="text-gray-600 mb-4">
                  Our system automatically tracks referrals, conversions, and rewards so you can focus on growing your business.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Real-time tracking</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Automated payouts</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span className="text-gray-700">Detailed reporting</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Key Features Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-10 text-primary-blue">Key Features</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <FeatureCard 
                title="Easy Sharing" 
                description="Referrers can share via email, social media, and messaging apps with just a few clicks." 
                icon={<Share2 className="h-6 w-6 text-primary-blue" />} 
              />
              
              <FeatureCard 
                title="User Management" 
                description="Manage all your referrers and their activities from a central dashboard." 
                icon={<Users className="h-6 w-6 text-primary-blue" />} 
              />
              
              <FeatureCard 
                title="Reward Flexibility" 
                description="Create custom reward structures including cash, points, discounts, and more." 
                icon={<Award className="h-6 w-6 text-primary-blue" />} 
              />
              
              <FeatureCard 
                title="Advanced Analytics" 
                description="Track performance, identify top referrers, and optimize your program with detailed insights." 
                icon={<BarChart4 className="h-6 w-6 text-primary-blue" />} 
              />
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-10 rounded-xl text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Growing with Referrals?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              All plans include a 7-day trial. Experience how our platform can help you acquire 
              more customers at a lower cost.
            </p>
            <a 
              href="/business-signup" 
              className="bg-white text-primary-blue px-8 py-3 rounded-full font-bold text-lg inline-flex items-center transition-transform hover:scale-105"
            >
              Start Your Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default HowItWorks;
